import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, stageLabel } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { PriorityBadge } from "../components/Badges";

function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function iso(d) { return d.toISOString().slice(0, 10); }

export default function InstallCalendar() {
  const [projects, setProjects] = useState([]);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); }, []);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const days = daysInMonth(year, month);

  const byDate = {};
  projects.forEach((p) => {
    if (p.installation_status === "scheduled" || p.installation_status === "in_progress") {
      const key = p.due_date;
      if (!key) return;
      byDate[key] = byDate[key] || [];
      byDate[key].push(p);
    }
  });

  const monthName = cursor.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d));

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Installations</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{monthName}</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/projects?new=1"><Button data-testid="new-installation-btn"><Plus className="h-4 w-4 mr-2" />New Installation</Button></Link>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setCursor(new Date())}>Today</Button>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Card className="p-4 bg-card border-border">
        <div className="grid grid-cols-7 gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="text-center font-semibold">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {cells.map((d, i) => {
            if (!d) return <div key={`empty-${year}-${month}-${i}`} className="min-h-24" />;
            const key = iso(d);
            const events = byDate[key] || [];
            const isToday = key === iso(new Date());
            return (
              <div key={key} className={`min-h-24 rounded border p-2 ${isToday ? "border-primary/60 bg-primary/5" : "border-border bg-secondary/20"}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-mono ${isToday ? "text-primary font-bold" : "text-muted-foreground"}`}>{d.getDate()}</span>
                </div>
                <div className="space-y-1 mt-1">
                  {events.map((p) => (
                    <div key={p.id} className="text-[10px] p-1 rounded bg-primary/10 border border-primary/30 text-primary truncate" title={p.client_name}>
                      {p.client_name}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 bg-card border-border">
        <h3 className="font-display font-semibold mb-3">All Scheduled Installations</h3>
        <div className="space-y-2">
          {projects.filter((p) => ["scheduled", "in_progress"].includes(p.installation_status)).map((p) => (
            <div key={p.id} className="flex items-center gap-3 p-2.5 rounded border border-border bg-secondary/30 card-hover">
              <div className="flex-1">
                <div className="text-sm font-medium">{p.client_name}</div>
                <div className="text-xs text-muted-foreground">{p.city} · {stageLabel(p.stage)} · {p.due_date || "TBD"}</div>
              </div>
              <span className="text-xs capitalize text-primary">{p.installation_status.replace("_", " ")}</span>
              <PriorityBadge value={p.priority} />
              <span className="text-xs font-mono text-primary">{inr(p.project_value)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
